/**
 * Namespaced Storage Hook
 * 
 * React hook that provides reactive access to the namespaced storage system
 * with automatic migration, TTL handling, and community awareness.
 * Replaces direct localStorage usage throughout the application.
 * 
 * Phase 4 implementation - reactive namespaced storage with migration
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { currentHostnameAtom } from '@/lib/state/brandingAtoms';
import {
  setNamespacedStorage,
  getNamespacedStorage,
  removeNamespacedStorage,
  clearNamespaceStorage,
  migrateLegacyKeys,
  needsMigration,
  inspectNamespacedStorage,
  type StorageOptions,
  type StorageNamespace,
  type StorageResult,
} from '@/lib/services/namespacedStorageService';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Hook for reactive namespaced storage
 */
export function useNamespacedStorage<T>(
  keyName: string,
  defaultValue?: T,
  options: StorageOptions = {}
) {
  const [value, setValue] = useState<T | undefined>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentHostname = useAtomValue(currentHostnameAtom);
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Enhanced options with community awareness
  const enhancedOptions: StorageOptions = {
    ...options,
    namespace: {
      community: currentHostname,
      ...options.namespace,
    }
  };

  /**
   * Load value from storage
   */
  const loadValue = useCallback(async () => {
    if (!isMounted.current) return;
    
    setLoading(true);
    setError(null);

    try {
      const result = getNamespacedStorage<T>(keyName, enhancedOptions);
      
      if (!isMounted.current) return;

      if (result.success && result.data !== undefined) {
        setValue(result.data);
      } else if (result.expired) {
        // Data expired, use default value
        setValue(defaultValue);
        setError('Data expired');
      } else {
        // Key not found, use default value
        setValue(defaultValue);
        if (result.error !== 'Key not found') {
          setError(result.error || null);
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setError(`Failed to load ${keyName}: ${err}`);
        setValue(defaultValue);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [keyName, defaultValue, enhancedOptions, currentHostname]);

  /**
   * Set value in storage
   */
  const setStoredValue = useCallback(async (newValue: T): Promise<StorageResult<T>> => {
    const result = setNamespacedStorage(keyName, newValue, enhancedOptions);
    
    if (result.success) {
      setValue(newValue);
      setError(null);
    } else {
      setError(result.error || 'Failed to set value');
    }
    
    return result;
  }, [keyName, enhancedOptions]);

  /**
   * Remove value from storage
   */
  const removeStoredValue = useCallback(async (): Promise<StorageResult<void>> => {
    const result = removeNamespacedStorage(keyName, enhancedOptions);
    
    if (result.success) {
      setValue(defaultValue);
      setError(null);
    } else {
      setError(result.error || 'Failed to remove value');
    }
    
    return result;
  }, [keyName, defaultValue, enhancedOptions]);

  /**
   * Refresh value from storage
   */
  const refresh = useCallback(() => {
    loadValue();
  }, [loadValue]);

  // Load initial value
  useEffect(() => {
    loadValue();
  }, [loadValue]);

  return {
    value,
    setValue: setStoredValue,
    removeValue: removeStoredValue,
    refresh,
    loading,
    error,
    // Computed properties
    hasValue: value !== undefined,
    isDefault: value === defaultValue,
  };
}

/**
 * Hook for managing namespaced storage migration
 */
export function useStorageMigration() {
  const [migrationStatus, setMigrationStatus] = useState<'pending' | 'running' | 'completed' | 'error'>('pending');
  const [migrationResult, setMigrationResult] = useState<{
    migrated: string[];
    errors: string[];
  } | null>(null);

  /**
   * Check if migration is needed
   */
  const checkMigration = useCallback((): boolean => {
    return needsMigration();
  }, []);

  /**
   * Run migration
   */
  const runMigration = useCallback(async () => {
    if (migrationStatus === 'running') {
      return; // Already running
    }

    setMigrationStatus('running');

    try {
      const result = await migrateLegacyKeys();
      
      if (result.success) {
        setMigrationStatus('completed');
        setMigrationResult(result.data || { migrated: [], errors: [] });
      } else {
        setMigrationStatus('error');
        setMigrationResult(result.data || { migrated: [], errors: ['Migration failed'] });
      }

      if (isDevelopment) {
        console.log('StorageMigration: Migration completed', result);
      }
    } catch (error) {
      setMigrationStatus('error');
      setMigrationResult({ migrated: [], errors: [`Migration exception: ${error}`] });
    }
  }, [migrationStatus]);

  // Auto-run migration on mount if needed
  useEffect(() => {
    if (checkMigration()) {
      runMigration();
    } else {
      setMigrationStatus('completed');
    }
  }, [checkMigration, runMigration]);

  return {
    migrationStatus,
    migrationResult,
    needsMigration: checkMigration,
    runMigration,
  };
}

/**
 * Hook for namespace-aware cache invalidation
 */
export function useNamespaceInvalidation() {
  const currentHostname = useAtomValue(currentHostnameAtom);

  /**
   * Clear entire namespace
   */
  const clearNamespace = useCallback(async (
    namespace: Partial<StorageNamespace> = {}
  ): Promise<StorageResult<string[]>> => {
    const enhancedNamespace = {
      community: currentHostname,
      ...namespace,
    };

    return clearNamespaceStorage(enhancedNamespace);
  }, [currentHostname]);

  /**
   * Clear current community namespace
   */
  const clearCommunityNamespace = useCallback(async (): Promise<StorageResult<string[]>> => {
    return clearNamespaceStorage({
      community: currentHostname,
      global: false,
    });
  }, [currentHostname]);

  /**
   * Clear global namespace only
   */
  const clearGlobalNamespace = useCallback(async (): Promise<StorageResult<string[]>> => {
    return clearNamespaceStorage({
      global: true,
    });
  }, []);

  /**
   * Clear all namespaces (nuclear option)
   */
  const clearAllNamespaces = useCallback(async (): Promise<StorageResult<string[]>> => {
    return clearNamespaceStorage({});
  }, []);

  return {
    clearNamespace,
    clearCommunityNamespace,
    clearGlobalNamespace,
    clearAllNamespaces,
    currentCommunity: currentHostname,
  };
}

/**
 * Hook for development utilities
 */
export function useNamespacedStorageDevUtils() {
  if (!isDevelopment) {
    return {
      inspect: () => console.warn('Development utilities not available in production'),
    };
  }

  /**
   * Inspect current storage state
   */
  const inspect = useCallback(() => {
    inspectNamespacedStorage();
  }, []);

  /**
   * Get storage stats
   */
  const getStats = useCallback(() => {
    const allKeys = Object.keys(localStorage);
    const namespacedKeys = allKeys.filter(key => key.startsWith('terrarium:'));
    const legacyKeys = allKeys.filter(key => 
      key.startsWith('terrarium_') && !key.startsWith('terrarium:')
    );

    return {
      totalKeys: allKeys.length,
      namespacedKeys: namespacedKeys.length,
      legacyKeys: legacyKeys.length,
      migrationNeeded: needsMigration(),
    };
  }, []);

  /**
   * Clear all terrarium storage (destructive)
   */
  const clearAllTerrariumStorage = useCallback(() => {
    const allKeys = Object.keys(localStorage);
    const terrariumKeys = allKeys.filter(key => key.startsWith('terrarium'));
    
    terrariumKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(`Cleared ${terrariumKeys.length} terrarium storage keys`);
    return terrariumKeys;
  }, []);

  return {
    inspect,
    getStats,
    clearAllTerrariumStorage,
  };
}

/**
 * Typed storage hooks for common use cases
 */

// Branding storage
export function useBrandingStorage() {
  return useNamespacedStorage('community_branding', null, {
    ttl: 60 * 60 * 1000, // 1 hour
  });
}

// User preferences storage (global)
export function useUserPreferencesStorage() {
  return useNamespacedStorage('user_preferences', {}, {
    namespace: { global: true },
  });
}

// Theme preference storage (global)
export function useThemePreferenceStorage() {
  return useNamespacedStorage('theme_preference', 'system', {
    namespace: { global: true },
  });
}

// Member onboarding storage (community-specific)
export function useMemberOnboardingStorage() {
  return useNamespacedStorage('member_onboarding', {
    currentStep: 'welcome',
    completed: {},
    profile: {},
  }, {
    ttl: 24 * 60 * 60 * 1000, // 24 hours
  });
}